import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Loader2, Pencil, Trash2, Plus, RefreshCw, Truck } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Stars } from "@/components/Stars";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api, { getAdminHeaders } from "@/lib/api";
import { formatBRL, categoryLabel, SUBCATEGORIAS_CELULARES } from "@/lib/format";

const emptyForm = {
  name: "",
  brand: "",
  category: "celulares",
  subcategory: "",
  colors: "",
  price: "",
  stock: "",
  image_url: "",
  description: "",
  specs: "",
  featured: false,
};

const statusBadge = {
  paid: { label: "Pago", className: "bg-cyan/15 text-cyan border-cyan/40" },
  shipped: { label: "Enviado", className: "bg-emerald-400/10 text-emerald-300 border-emerald-400/40" },
  pending: { label: "Pendente", className: "bg-amber-400/10 text-amber-300 border-amber-400/40" },
  failed: { label: "Falhou", className: "bg-red-500/10 text-red-300 border-red-500/40" },
  expired: { label: "Expirado", className: "bg-white/5 text-mute border-white/20" },
};

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [passInput, setPassInput] = useState("");
  const [checking, setChecking] = useState(false);

  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [answers, setAnswers] = useState({});
  const [answeringId, setAnsweringId] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [shippingId, setShippingId] = useState(null);

  const markShipped = async (id) => {
    setShippingId(id);
    try {
      const { data } = await api.post(`/orders/${id}/ship`, {}, { headers: getAdminHeaders() });
      if (data.already) {
        toast.info("Este pedido já estava marcado como enviado.");
      } else {
        toast.success("Pedido marcado como enviado — cliente avisado por e-mail!");
      }
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível atualizar o pedido.");
    } finally {
      setShippingId(null);
    }
  };

  const answerQuestion = async (id) => {
    const answer = (answers[id] || "").trim();
    if (answer.length < 2) {
      toast.error("Escreva a resposta antes de enviar.");
      return;
    }
    setAnsweringId(id);
    try {
      await api.post(`/questions/${id}/answer`, { answer }, { headers: getAdminHeaders() });
      toast.success("Resposta publicada no produto!");
      setAnswers((a) => ({ ...a, [id]: "" }));
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível responder.");
    } finally {
      setAnsweringId(null);
    }
  };

  const removeReview = async (id) => {
    try {
      await api.delete(`/reviews/${id}`, { headers: getAdminHeaders() });
      toast.success("Avaliação removida.");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível remover a avaliação.");
    }
  };

  const loadAll = async () => {
    try {
      const [p, o, q, r] = await Promise.all([
        api.get("/products"),
        api.get("/orders", { headers: getAdminHeaders() }),
        api.get("/admin/questions", { headers: getAdminHeaders() }),
        api.get("/admin/reviews", { headers: getAdminHeaders() }),
      ]);
      setProducts(p.data);
      setOrders(o.data);
      setQuestions(q.data.questions || []);
      setReviews(r.data.reviews || []);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem("vortex_admin_pass");
        setAuthed(false);
      } else {
        toast.error("Erro ao carregar dados do painel.");
      }
    }
  };

  useEffect(() => {
    const saved = sessionStorage.getItem("vortex_admin_pass");
    if (!saved) {
      setAuthed(false);
      return;
    }
    api
      .get("/orders", { headers: getAdminHeaders() })
      .then(() => {
        setAuthed(true);
        loadAll();
      })
      .catch(() => {
        sessionStorage.removeItem("vortex_admin_pass");
        setAuthed(false);
      });
  }, []);

  const tryAuth = async (e) => {
    e.preventDefault();
    setChecking(true);
    sessionStorage.setItem("vortex_admin_pass", passInput);
    try {
      await api.get("/orders", { headers: getAdminHeaders() });
      setAuthed(true);
      loadAll();
    } catch {
      sessionStorage.removeItem("vortex_admin_pass");
      toast.error("Senha incorreta.");
    } finally {
      setChecking(false);
    }
  };

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name,
      brand: p.brand || "",
      category: p.category,
      subcategory: p.subcategory || "",
      colors: (p.colors || []).join(", "),
      price: String(p.price),
      stock: String(p.stock),
      image_url: p.image_url || "",
      description: p.description || "",
      specs: (p.specs || []).join("\n"),
      featured: p.featured,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      subcategory: form.category === "celulares" ? form.subcategory : "",
      colors: form.colors.split(",").map((c) => c.trim()).filter(Boolean),
      price: parseFloat(form.price),
      stock: parseInt(form.stock || "0", 10),
      specs: form.specs.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editingId) {
        await api.put(`/products/${editingId}`, payload, { headers: getAdminHeaders() });
        toast.success("Produto atualizado.");
      } else {
        await api.post("/products", payload, { headers: getAdminHeaders() });
        toast.success("Produto criado.");
      }
      setDialogOpen(false);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar produto.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/products/${id}`, { headers: getAdminHeaders() });
      toast.success("Produto removido.");
      setConfirmId(null);
      loadAll();
    } catch {
      toast.error("Erro ao remover produto.");
    }
  };

  if (authed === null) {
    return (
      <div className="bg-ink min-h-screen flex items-center justify-center">
        <Loader2 size={30} className="text-cyan animate-spin" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="bg-ink min-h-screen flex items-center justify-center px-6">
        <form
          onSubmit={tryAuth}
          className="w-full max-w-sm bg-surface border border-white/10 rounded-lg p-8"
          data-testid="admin-gate"
        >
          <div className="h-12 w-12 rounded-md bg-cyan/10 border border-cyan/40 flex items-center justify-center">
            <Lock size={20} className="text-cyan" />
          </div>
          <h1 className="font-display font-semibold text-2xl mt-5">
            Painel do administrador
          </h1>
          <p className="text-mute text-sm mt-2">
            Digite a senha de acesso para gerenciar o catálogo e os pedidos.
          </p>
          <Input
            type="password"
            value={passInput}
            onChange={(e) => setPassInput(e.target.value)}
            placeholder="Senha do painel"
            className="mt-6 h-12 bg-elevated border-white/15"
            data-testid="admin-gate-input"
          />
          <Button
            type="submit"
            disabled={checking || !passInput}
            className="w-full h-12 mt-4 bg-cyan text-ink font-semibold hover:bg-cyan-dark"
            data-testid="admin-gate-submit"
          >
            {checking ? <Loader2 size={18} className="animate-spin" /> : "Entrar"}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="bg-ink min-h-screen">
      <div className="mx-auto max-w-7xl px-5 lg:px-10 pt-28 md:pt-32 pb-20">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="flex items-center gap-3 text-cyan text-xs uppercase tracking-[0.22em] mb-4">
              <span className="h-px w-10 bg-cyan inline-block" />
              Administração
            </p>
            <h1 className="font-display font-semibold tracking-tight text-4xl md:text-5xl">
              Gestão da loja
            </h1>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={loadAll}
              className="border-white/20 hover:border-cyan hover:text-cyan"
              data-testid="admin-refresh-button"
            >
              <RefreshCw size={16} />
              Atualizar
            </Button>
            <Button
              onClick={openNew}
              className="bg-cyan text-ink font-semibold hover:bg-cyan-dark"
              data-testid="admin-add-product-button"
            >
              <Plus size={16} />
              Novo produto
            </Button>
          </div>
        </div>

        <Tabs defaultValue="produtos" className="mt-10">
          <TabsList className="bg-elevated border border-white/10">
            <TabsTrigger value="produtos" data-testid="admin-tab-produtos">
              Produtos
            </TabsTrigger>
            <TabsTrigger value="pedidos" data-testid="admin-tab-pedidos">
              Pedidos
            </TabsTrigger>
            <TabsTrigger value="interacao" data-testid="admin-tab-interacao">
              Interação
            </TabsTrigger>
          </TabsList>

          <TabsContent value="produtos">
            <div className="mt-6 rounded-lg border border-white/10 overflow-x-auto">
              <Table data-testid="admin-products-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead>Produto</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Preço</TableHead>
                    <TableHead>Estoque</TableHead>
                    <TableHead>Destaque</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((p) => (
                    <TableRow key={p.id} className="border-white/10" data-testid={`admin-product-row-${p.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <img
                            src={p.image_url}
                            alt=""
                            className="h-10 w-10 rounded object-cover bg-elevated"
                          />
                          <div>
                            <p className="font-medium text-sm">{p.name}</p>
                            {p.brand && (
                              <p className="text-xs text-mute">{p.brand}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-mute">
                        {categoryLabel(p.category)}
                      </TableCell>
                      <TableCell className="text-sm">{formatBRL(p.price)}</TableCell>
                      <TableCell className="text-sm">{p.stock}</TableCell>
                      <TableCell>
                        {p.featured ? (
                          <Badge className="bg-cyan/15 text-cyan border-cyan/40 hover:bg-cyan/15">
                            Sim
                          </Badge>
                        ) : (
                          <span className="text-mute text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-white/15 hover:border-cyan hover:text-cyan"
                            onClick={() => openEdit(p)}
                            data-testid={`admin-edit-button-${p.id}`}
                          >
                            <Pencil size={14} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              confirmId === p.id ? remove(p.id) : setConfirmId(p.id)
                            }
                            className={`border-white/15 ${
                              confirmId === p.id
                                ? "bg-destructive text-white border-destructive"
                                : "hover:border-destructive hover:text-destructive"
                            }`}
                            data-testid={`admin-delete-button-${p.id}`}
                          >
                            <Trash2 size={14} />
                            {confirmId === p.id && "Confirmar"}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {products.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-mute py-10">
                        Nenhum produto cadastrado ainda.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="pedidos">
            <div className="mt-6 rounded-lg border border-white/10 overflow-x-auto">
              <Table data-testid="admin-orders-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead>Data</TableHead>
                    <TableHead>Itens</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Pagamento</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((o) => {
                    const badge = statusBadge[o.status] || statusBadge.pending;
                    return (
                      <TableRow key={o.id} className="border-white/10" data-testid={`admin-order-row-${o.id}`}>
                        <TableCell className="text-sm whitespace-nowrap">
                          {new Date(o.created_at).toLocaleString("pt-BR", {
                            day: "2-digit",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </TableCell>
                        <TableCell className="text-sm text-mute max-w-[280px]">
                          {o.items.map((i) => `${i.qty || i.quantity}x ${i.name}${i.color ? ` (${i.color})` : ""}`).join(", ")}
                        </TableCell>
                        <TableCell className="text-sm font-medium">
                          {formatBRL(o.total)}
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="font-medium" data-testid="admin-order-customer-name">
                            {o.customer_name || "—"}
                          </div>
                          <div className="text-xs text-mute">{o.customer_email || ""}</div>
                          {o.customer_phone && (
                            <div className="text-xs text-mute">{o.customer_phone}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-mute" data-testid="admin-order-payment">
                          {o.payment_method === "pix"
                            ? "Pix"
                            : o.payment_method === "bolbradesco"
                              ? "Boleto"
                              : o.payment_method === "card"
                                ? `Cartão${o.installments ? ` • ${o.installments}x` : ""}`
                                : "—"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={badge.className}>
                            {badge.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {o.status === "paid" && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={shippingId === o.id}
                              onClick={() => markShipped(o.id)}
                              className="h-8 border-cyan/40 text-cyan hover:bg-cyan/10"
                              data-testid={`admin-ship-button-${o.id}`}
                            >
                              <Truck size={14} className="mr-1" />
                              {shippingId === o.id ? "Enviando..." : "Marcar enviado"}
                            </Button>
                          )}
                          {o.status === "shipped" && (
                            <span className="text-xs text-mute" data-testid={`admin-shipped-at-${o.id}`}>
                              {o.shipped_at
                                ? new Date(o.shipped_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })
                                : "—"}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {orders.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-mute py-10">
                        Nenhum pedido recebido ainda.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="interacao">
            <h3 className="font-display text-xl mt-8">Perguntas dos clientes</h3>
            <div className="mt-4 rounded-lg border border-white/10 overflow-x-auto">
              <Table data-testid="admin-questions-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead>Data</TableHead>
                    <TableHead>Produto</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Pergunta</TableHead>
                    <TableHead>Resposta</TableHead>
                    <TableHead>Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {questions.map((q) => (
                    <TableRow key={q.id} className="border-white/10" data-testid={`admin-question-row-${q.id}`}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {new Date(q.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}
                      </TableCell>
                      <TableCell className="text-sm max-w-[180px]">{q.product_name}</TableCell>
                      <TableCell className="text-sm">{q.name}</TableCell>
                      <TableCell className="text-sm text-mute max-w-[240px]">{q.question}</TableCell>
                      <TableCell className="text-sm max-w-[240px]">
                        {q.answer ? (
                          <span data-testid={`admin-question-answer-${q.id}`} className="text-cyan/90">{q.answer}</span>
                        ) : (
                          <input
                            value={answers[q.id] || ""}
                            onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                            placeholder="Escreva a resposta..."
                            maxLength={800}
                            className="w-full h-9 rounded-md bg-ink/60 border border-white/15 px-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-cyan/60"
                            data-testid={`admin-answer-input-${q.id}`}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {q.answer ? (
                          <span className="text-xs text-mute">respondida</span>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={answeringId === q.id}
                            onClick={() => answerQuestion(q.id)}
                            className="h-8 border-cyan/40 text-cyan hover:bg-cyan/10"
                            data-testid={`admin-answer-button-${q.id}`}
                          >
                            {answeringId === q.id ? "Enviando..." : "Responder"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {questions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-mute py-10">
                        Nenhuma pergunta recebida ainda.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <h3 className="font-display text-xl mt-12">Avaliações dos clientes</h3>
            <div className="mt-4 rounded-lg border border-white/10 overflow-x-auto">
              <Table data-testid="admin-reviews-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead>Data</TableHead>
                    <TableHead>Produto</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Nota</TableHead>
                    <TableHead>Comentário</TableHead>
                    <TableHead>Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviews.map((r) => (
                    <TableRow key={r.id} className="border-white/10" data-testid={`admin-review-row-${r.id}`}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {new Date(r.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}
                      </TableCell>
                      <TableCell className="text-sm max-w-[180px]">{r.product_name}</TableCell>
                      <TableCell className="text-sm">{r.name}</TableCell>
                      <TableCell>
                        <Stars value={r.rating} size={12} />
                      </TableCell>
                      <TableCell className="text-sm text-mute max-w-[240px]">
                        {r.comment || "—"}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => removeReview(r.id)}
                          className="h-8 border-destructive/40 text-destructive hover:bg-destructive/10"
                          data-testid={`admin-review-delete-${r.id}`}
                        >
                          <Trash2 size={14} className="mr-1" />
                          Excluir
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {reviews.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-mute py-10">
                        Nenhuma avaliação recebida ainda.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-surface border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editingId ? "Editar produto" : "Novo produto"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 mt-2">
            <div className="grid gap-2">
              <Label>Nome</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex.: iPhone 13 128GB"
                data-testid="product-form-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Categoria</Label>
                <Select
                  value={form.category}
                  onValueChange={(v) => setForm({ ...form, category: v })}
                >
                  <SelectTrigger data-testid="product-form-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-elevated border-white/10">
                    <SelectItem value="celulares">Celulares</SelectItem>
                    <SelectItem value="acessorios">Acessórios</SelectItem>
                    <SelectItem value="telas">Telas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Marca</Label>
                <Input
                  value={form.brand}
                  onChange={(e) => setForm({ ...form, brand: e.target.value })}
                  placeholder="Ex.: Samsung"
                  data-testid="product-form-brand"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Preço (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="1499.00"
                  data-testid="product-form-price"
                />
              </div>
              <div className="grid gap-2">
                <Label>Estoque</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.stock}
                  onChange={(e) => setForm({ ...form, stock: e.target.value })}
                  placeholder="10"
                  data-testid="product-form-stock"
                />
              </div>
            </div>
            {form.category === "celulares" && (
              <div className="grid gap-2">
                <Label>Subcategoria</Label>
                <Select
                  value={form.subcategory}
                  onValueChange={(v) => setForm({ ...form, subcategory: v })}
                >
                  <SelectTrigger data-testid="product-form-subcategory">
                    <SelectValue placeholder="Selecione a linha" />
                  </SelectTrigger>
                  <SelectContent className="bg-elevated border-white/10">
                    {SUBCATEGORIAS_CELULARES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid gap-2">
              <Label>Cores disponíveis (separadas por vírgula)</Label>
              <Input
                value={form.colors}
                onChange={(e) => setForm({ ...form, colors: e.target.value })}
                placeholder="Ex.: Preto, Prata, Azul"
                data-testid="product-form-colors"
              />
            </div>
            <div className="grid gap-2">
              <Label>URL da imagem</Label>
              <Input
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="https://..."
                data-testid="product-form-image"
              />
            </div>
            <div className="grid gap-2">
              <Label>Descrição</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                placeholder="Descrição exibida na página do produto"
                data-testid="product-form-description"
              />
            </div>
            <div className="grid gap-2">
              <Label>Especificações (uma por linha)</Label>
              <Textarea
                value={form.specs}
                onChange={(e) => setForm({ ...form, specs: e.target.value })}
                rows={3}
                placeholder={"Tela 6.6\" AMOLED\n256GB de armazenamento"}
                data-testid="product-form-specs"
              />
            </div>
            <div className="flex items-center justify-between rounded-md border border-white/10 p-3">
              <Label>Produto em destaque</Label>
              <Switch
                checked={form.featured}
                onCheckedChange={(v) => setForm({ ...form, featured: v })}
                data-testid="product-form-featured"
              />
            </div>
            <Button
              onClick={save}
              disabled={saving || !form.name || !form.price}
              className="h-12 bg-cyan text-ink font-semibold hover:bg-cyan-dark"
              data-testid="product-form-save"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : "Salvar produto"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
